/**
 * PATH: packages/job-hub/src/jobs/tabs/job-qa/JobQaSetupSection.tsx
 * FILE_ID: JOBHUB-QA-SETUP-SECTION-001
 * PURPOSE: Assemble the Set up strip, quote body, and create-from-quotes gate.
 */
import { useEffect, useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { usePersistentBool } from '@quotech/ui'
import { JobQaSetupCard } from '../JobQaSetupCard'
import { JobQaAddQuotesSlot } from './JobQaAddQuotesSlot'
import { JobQaSetupStrip, type QaSetupOption } from './JobQaSetupStrip'
import { JobQaSetupNameDialog } from './JobQaSetupNameDialog'
import { hideQaQuote } from './useQaJobQuotes'
import { QaUseTemplatesDialog } from './QaUseTemplatesDialog'
import type { QaSetupNameAction } from './JobQaSetupNameDialog'
import { resolveQaSetupCollapsed } from './qaSetupCollapsed'
import type { JobQaTabController } from './useJobQaTabController'

const STRIP_KEY = 'qa.setupStrip.collapsed'

function readStripStored(): string | null {
  if (typeof window === 'undefined') return null
  try {
    return window.localStorage.getItem(STRIP_KEY)
  } catch {
    return null
  }
}

export function JobQaSetupSection({
  controller,
  forceExpanded = false,
  page = false,
  onOpenTemplates,
  onCloseTemplates,
}: {
  controller: JobQaTabController
  forceExpanded?: boolean
  /** Phone Set Up page: quote flow fills the page; Quote Details is a sheet. */
  page?: boolean
  onOpenTemplates?: () => void
  onCloseTemplates?: () => void
}) {
  const { creation, error, props, workspace } = controller
  const queryClient = useQueryClient()
  const hasInspections = workspace.inspections.length > 0
  const [persisted, setPersisted] = usePersistentBool(STRIP_KEY, false)
  const expanded = forceExpanded || page
  const collapsed = expanded
    ? false
    : resolveQaSetupCollapsed(hasInspections, readStripStored(), persisted)
  const [option, setOption] = useState<QaSetupOption | null>(null)
  const [askTemplates, setAskTemplates] = useState(false)
  const [nameAsk, setNameAsk] = useState<null | { kind: 'templates' | 'quotes' }>(null)
  const [namePending, setNamePending] = useState(false)
  const userChose = useRef(false)
  const highlighted = useRef(false)
  const highlightedOnly = useRef(false)
  const openTemplates = () => {
    if (page) onOpenTemplates?.()
    else controller.requestOpenTemplates()
  }
  const pick = (next: QaSetupOption) => {
    userChose.current = true
    const closing = option === next && !highlightedOnly.current
    highlightedOnly.current = false
    if (closing) {
      setOption(null)
      setNameAsk(null)
      if (next === 'templates' && page) onCloseTemplates?.()
      return
    }
    if (next === 'templates') {
      setOption('templates')
      openTemplates()
      return
    }
    setOption('quotes')
  }
  const confirmName = async (action: QaSetupNameAction, name: string) => {
    const kind = nameAsk?.kind
    if (!kind) return
    setNamePending(true)
    try {
      if (action === 'add') {
        creation.setWriteTarget('add')
        controller.rememberSetupWrite('add')
      } else if (action === 'create') {
        creation.setWriteTarget('create')
        creation.setScopeName(name)
        controller.rememberSetupWrite('add')
      } else if (workspace.activeInspectionId) {
        const current = workspace.inspections.find((row) => row.id === workspace.activeInspectionId)
        if (current && current.inspection_name !== name) {
          await workspace.renameInspection.mutateAsync({ id: workspace.activeInspectionId, name })
        }
        creation.setWriteTarget('replace')
        controller.rememberSetupWrite('replace')
      }
      setNameAsk(null)
      setOption('quotes')
      openCreate()
    } finally {
      setNamePending(false)
    }
  }
  useEffect(() => {
    if (collapsed) {
      userChose.current = false
      highlighted.current = false
      highlightedOnly.current = false
      return
    }
    if (userChose.current || highlighted.current) return
    if (workspace.inspections.length > 0 || props.quotesLoading) return
    highlighted.current = true
    highlightedOnly.current = true
    if (props.quotes.length > 0) {
      setOption('quotes')
      return
    }
    setOption('templates')
  }, [
    collapsed,
    props.quotes.length,
    props.quotesLoading,
    workspace.inspections.length,
  ])
  if (controller.canBuild === false) return null
  const activeInspection = workspace.inspections.find((row) => row.id === workspace.activeInspectionId)
  const openCreate = () => {
    setAskTemplates(true)
  }
  return (
    <>
      <JobQaSetupStrip
        collapsed={collapsed}
        forceExpanded={expanded}
        onToggle={() => {
          setPersisted(!collapsed)
          if (!collapsed) setOption(null)
        }}
        option={collapsed ? null : option}
        onOption={pick}
      >
        {option === 'quotes' && (
          <div className="mt-3">
            <JobQaSetupCard
              embedded
              quotes={props.quotes}
              quotesLoading={props.quotesLoading ?? false}
              quotesError={controller.quotesError}
              onRetryQuotes={() => { void controller.refetchJobQuotes() }}
              onOpenQuote={props.onOpenQuote}
              selectedSheetIds={creation.selectedSheetIds}
              onToggleSheet={creation.toggleSheet}
              onRemoveQuote={(id) => {
                void hideQaQuote(controller.client, props.jobId, id).then(() => {
                  void queryClient.invalidateQueries({ queryKey: ['quotes', { job_id: props.jobId }] })
                  void controller.refetchJobQuotes()
                })
              }}
              classification={creation.classification}
              suggestion={creation.suggestion}
              onClassificationChange={creation.handleClassificationChange}
              createMode={creation.createMode}
              onCreateModeChange={creation.setCreateMode}
              writeTarget={creation.writeTarget}
              onWriteTargetChange={creation.setWriteTarget}
              showWriteTarget={false}
              includeQuoteDerivedItems={creation.includeQuoteDerivedItems}
              onIncludeQuoteDerivedItemsChange={creation.setIncludeQuoteDerivedItems}
              scopeName={creation.scopeName}
              onScopeNameChange={creation.setScopeName}
              onCreate={openCreate}
              onCombine={() => setNameAsk({ kind: 'quotes' })}
              createPending={creation.create.isPending}
              createProgress={creation.createProgress}
              createNotice={creation.createNotice}
              genError={error}
              onRetryImport={creation.retryImport}
              retryingImportId={creation.retryingImportId}
              retryImportError={creation.retryImportError}
              addQuotes={<JobQaAddQuotesSlot controller={controller} />}
              hasInspections={hasInspections}
              fillPage={page}
            />
          </div>
        )}
      </JobQaSetupStrip>
      {nameAsk ? (
        <JobQaSetupNameDialog
          selectedName={activeInspection?.inspection_name ?? ''}
          pending={namePending}
          onCancel={() => setNameAsk(null)}
          onConfirm={(action, name) => { void confirmName(action, name) }}
        />
      ) : null}
      {askTemplates && (
        <QaUseTemplatesDialog
          onCancel={() => setAskTemplates(false)}
          onContinue={(yes) => {
            creation.setUseTemplates(yes)
            setAskTemplates(false)
            creation.confirmCreate({ useTemplates: yes })
          }}
        />
      )}
    </>
  )
}
